import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet as WalletIcon, Gift, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageMeta } from "@/lib/site";
import { walletApi } from "@/lib/accountApi";
import { gbp, prettyDate } from "@/lib/format";
import { Empty } from "@/components/partner/PartnerShell";

const TXN_LABEL = { topup: "Top-up", gift_redeem: "Gift card redeemed", booking: "Booking", refund: "Refund", payout: "Payout", package: "Package", membership: "Membership", loyalty: "Points" };

export default function Wallet() {
  usePageMeta("Wallet", "Your Fable + wallet balance, points, packages and memberships.");
  const [w, setW] = useState(undefined);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => walletApi.get().then(setW).catch(() => setW(null));
  useEffect(() => {
    load();
  }, []);

  const redeem = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const r = await walletApi.redeemGiftCard(code.trim());
      toast.success(`Added ${gbp(r.amount)} to your wallet`);
      setCode("");
      load();
    } catch (err) {
      toast.error(err.message || "That code didn't work.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <h1 className="font-serif-display text-3xl font-bold">Wallet</h1>
      {w?.is_test && (
        <p className="mt-3 inline-flex items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-900" data-testid="wallet-test-banner">
          <FlaskConical size={14} /> Test payments · no real card charges. This sandbox balance is separate from your real money.
        </p>
      )}
      {w === undefined ? (
        <div className="mt-6 grid gap-4 sm:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>
      ) : w === null ? (
        <p className="mt-10 text-center text-muted-foreground">Couldn't load your wallet.</p>
      ) : (
        <>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            <div className="rounded-2xl p-5 text-white sm:col-span-2" style={{ background: "var(--fable)" }} data-testid="wallet-balance">
              <p className="inline-flex items-center gap-2 text-sm font-medium text-white/85"><WalletIcon size={16} /> Balance</p>
              <p className="mt-2 font-mono-num text-4xl font-bold">{gbp(w.balance)}</p>
              <p className="mt-3 text-sm text-white/85">Refunds, gift cards and loyalty credit land here and come off your next booking.</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Points</p>
              <p className="mt-2 font-serif-display text-3xl font-bold">{w.points}</p>
              <p className="mt-1 text-xs text-muted-foreground">{w.points_frozen ? "Worth " + gbp(w.points_value) + " · redeeming opens soon" : "Worth " + gbp(w.points_value)}</p>
            </div>
          </div>

          <form onSubmit={redeem} className="mt-4 flex flex-col gap-2 rounded-2xl border border-border bg-card p-4 sm:flex-row sm:items-center">
            <p className="inline-flex items-center gap-2 text-sm font-semibold"><Gift size={16} /> Redeem a gift card</p>
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Gift card code" data-testid="gift-code" className="flex-1 rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
            <Button type="submit" className="rounded-full" disabled={busy || !code.trim()}>Redeem</Button>
          </form>

          {(w.packages?.length > 0 || w.memberships?.length > 0) && (
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {w.packages?.length > 0 && (
                <div className="rounded-2xl border border-border bg-card p-5">
                  <h2 className="font-bold">Packages</h2>
                  <ul className="mt-3 space-y-2 text-sm">
                    {w.packages.map((p) => (
                      <li key={p.id} className="flex items-center justify-between gap-3"><span className="truncate">{p.title || p.package_name || "Package"}</span><span className="font-mono-num text-muted-foreground">{p.remaining} left</span></li>
                    ))}
                  </ul>
                </div>
              )}
              {w.memberships?.length > 0 && (
                <div className="rounded-2xl border border-border bg-card p-5">
                  <h2 className="font-bold">Memberships</h2>
                  <ul className="mt-3 space-y-2 text-sm">
                    {w.memberships.map((m) => (
                      <li key={m.id} className="flex items-center justify-between gap-3"><span className="truncate">{m.title || m.membership_name || "Membership"}</span><span className="text-muted-foreground">{m.renews_at ? `renews ${prettyDate(m.renews_at)}` : "active"}</span></li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <section className="mt-6">
            <h2 className="font-bold">Activity</h2>
            {w.transactions.length === 0 ? (
              <div className="mt-3"><Empty title="No activity yet" body="Top-ups, bookings paid from your balance and refunds show here." action={<Link to="/marketplace"><Button variant="outline" className="rounded-full">Find something to book</Button></Link>} /></div>
            ) : (
              <ul className="mt-3 divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card" data-testid="wallet-txns">
                {w.transactions.map((t) => (
                  <li key={t.id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium">{TXN_LABEL[t.type] || t.type}</p>
                      <p className="text-xs text-muted-foreground">{prettyDate(t.created_at)}{t.ref ? ` · ${t.ref}` : ""}</p>
                    </div>
                    <span className={`font-mono-num font-semibold ${t.amount < 0 ? "" : "text-emerald-700"}`}>{t.amount < 0 ? "−" : "+"}{gbp(Math.abs(t.amount))}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
